export interface CompanyGrantSource {
	readonly companyId: string | null;
	readonly timestamp: string | null;
}

export interface PersonGrantSource {
	readonly companyId: string | null;
	readonly name: string | null;
	readonly timestamp: string | null;
}

export interface OptedOutPersonSource {
	readonly companyId: string | null;
	readonly name: string | null;
}

export interface CompanyFeatureSource {
	readonly id: string;
	readonly name: string | null;
	readonly rolledOut: boolean | null;
	readonly companies: ReadonlyArray<CompanyGrantSource | null> | null;
	readonly persons: ReadonlyArray<PersonGrantSource | null> | null;
	readonly personsOptedOut: ReadonlyArray<OptedOutPersonSource | null> | null;
}

/** Rows for the check-company modal, which joins these string arrays straight into its table. */
export interface CompanyFeatureFlagEntry {
	id: string;
	name: string;
	persons: string[];
	personsOptedOut: string[];
}

const isPresent = <T>(value: T | null | undefined): value is T => value != null;

const withDate = (label: string, timestamp: string | null): string =>
	timestamp ? `${label} (${timestamp.substring(0, 10)})` : label;

/**
 * Which not-yet-rolled-out flags a single company holds, and how.
 *
 * Rolled-out flags are omitted because everyone has them, so listing them says nothing about the
 * company. `encodedCompanyId` is a base64 `CompanyType:{n}` id, matching how grants are keyed.
 */
export function buildCompanyFeatureFlags(
	features: ReadonlyArray<CompanyFeatureSource | null> | null | undefined,
	encodedCompanyId: string
): CompanyFeatureFlagEntry[] {
	return (features ?? [])
		.filter(isPresent)
		.filter(feature => !feature.rolledOut)
		.map(feature => {
			const allUsersGrant = (feature.companies ?? [])
				.filter(isPresent)
				.find(company => company.companyId === encodedCompanyId);
			const namedPersons = (feature.persons ?? [])
				.filter(isPresent)
				.filter(person => person.companyId === encodedCompanyId);

			if (!allUsersGrant && namedPersons.length === 0) {
				return null;
			}

			return {
				id: feature.id,
				name: feature.name ?? '',
				persons: allUsersGrant
					? [withDate('All Users', allUsersGrant.timestamp)]
					: namedPersons.map(person => withDate(person.name ?? '', person.timestamp)),
				personsOptedOut: (feature.personsOptedOut ?? [])
					.filter(isPresent)
					.filter(person => person.companyId === encodedCompanyId)
					.map(person => person.name ?? ''),
			};
		})
		.filter(isPresent);
}
